// src/pages/abonnements/AbonnementsExpires.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Card, Spinner, Alert, Button, Table, Form, Badge } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import Header from '../../layouts/Header';
import Footer from '../../layouts/Footer';
import { COLORS } from '../../constants/colors';
import { Colors } from 'chart.js';

// ===== STYLES =====
const styles = `
  .dashboard-styled {
    background: #f4f6f9;
    min-height: 100vh;
  }

  /* En-tête moderne */
  .header-modern {
    background: white;
    padding: 1.2rem 1.8rem;
    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
    border-left: 4px solid ${COLORS.secondary};
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
  }

  .header-left {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  .header-icon-wrapper {
    width: 48px;
    height: 48px;
    background: ${COLORS.primary};
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    color: white;
    font-size: 1.4rem;
  }

  .gradient-text {
    color: #1e293b;
    font-size: 1.3rem;
    font-weight: 700;
    margin: 0;
  }

  .badge-modern {
    background: #f1f4f9;
    color: #475569;
    padding: 0.3rem 0.8rem;
    border-radius: 4px;
    font-size: 0.75rem;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  .badge-modern i {
    color: ${COLORS.secondary};
  }

  .btn-modern-outline {
    border: 1px solid #d1d5db;
    background: white;
    padding: 0.4rem 1.2rem;
    border-radius: 4px;
    font-size: 0.85rem;
    font-weight: 500;
    color: #4b5563;
    transition: all 0.2s;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  .btn-modern-outline:hover {
    border-color: ${COLORS.secondary};
    color: ${COLORS.secondary};
    background: #fafafa;
  }

  .btn-modern-primary {
    background: ${COLORS.primary};
    border: none;
    padding: 0.4rem 1.2rem;
    border-radius: 4px;
    font-size: 0.85rem;
    font-weight: 500;
    color: white;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    transition: all 0.2s;
  }
  .btn-modern-primary:hover {
    background: ${COLORS.primaryDark};
    box-shadow: 0 4px 10px rgba(41,90,167,0.3);
  }

  .btn-modern-success {
    background: ${COLORS.success};
    border: none;
    padding: 0.4rem 1.2rem;
    border-radius: 4px;
    font-size: 0.85rem;
    font-weight: 500;
    color: white;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    transition: all 0.2s;
  }
  .btn-modern-success:hover {
    background: #059669;
    box-shadow: 0 4px 10px rgba(16,185,129,0.3);
  }

  /* KPI Cards */
  .kpi-card {
    background: white;
    border-radius: 4px;
    padding: 1rem;
    display: flex;
    align-items: center;
    gap: 1rem;
    box-shadow: 0 2px 6px rgba(0,0,0,0.04);
    transition: all 0.2s ease;
    height: 100%;
    border-left: 3px solid;
  }
  .kpi-card:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 20px rgba(0,0,0,0.06);
  }
  .kpi-primary { border-left-color: ${COLORS.primary}; }
  .kpi-success { border-left-color: ${COLORS.success}; }
  .kpi-warning { border-left-color: ${COLORS.warning}; }
  .kpi-danger { border-left-color: ${COLORS.danger}; }
  .kpi-info { border-left-color: ${COLORS.info}; }

  .kpi-icon {
    width: 44px;
    height: 44px;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.2rem;
  }
  .kpi-primary .kpi-icon { background: ${COLORS.primary}15; color: ${COLORS.primary}; }
  .kpi-success .kpi-icon { background: ${COLORS.success}15; color: ${COLORS.success}; }
  .kpi-warning .kpi-icon { background: ${COLORS.warning}15; color: ${COLORS.warning}; }
  .kpi-danger .kpi-icon { background: ${COLORS.danger}15; color: ${COLORS.danger}; }
  .kpi-info .kpi-icon { background: ${COLORS.info}15; color: ${COLORS.info}; }

  .kpi-content { flex: 1; }
  .kpi-label {
    font-size: 0.7rem;
    text-transform: uppercase;
    color: #6c757d;
    font-weight: 600;
    letter-spacing: 0.5px;
  }
  .kpi-value {
    font-size: 1rem;
    font-weight: 700;
    margin: 0.1rem 0 0 0;
    color: #1e293b;
  }

  /* Filter card */
  .filter-card {
    border: none;
    border-radius: 4px;
    background: ${COLORS.primary};
    box-shadow: 0 2px 6px rgba(0,0,0,0.04);
  }
  .filter-card .form-control,
  .filter-card .form-select {
    border-radius: 4px;
    border: none;
    padding: 0.35rem 0.7rem;
    font-size: 0.8rem;
    background: rgba(255,255,255,0.95);
  }
  .filter-card .form-control:focus,
  .filter-card .form-select:focus {
    box-shadow: 0 0 0 3px rgba(255,255,255,0.2);
  }

  /* Table */
  .table-card {
    border: none;
    border-radius: 4px;
    background: white;
    box-shadow: 0 2px 6px rgba(0,0,0,0.04);
    overflow: hidden;
  }
  .table-header-secondary {
    background: ${COLORS.secondary};
    color: white;
    padding: 0.6rem 1rem;
  }
  .table-modern thead th {
    background: #f8fafc;
    color: #475569;
    font-weight: 600;
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    border-bottom: 1px solid #e2e8f0;
    white-space: nowrap;
  }
  .table-modern tbody tr {
    transition: all 0.2s ease;
  }
  .table-modern tbody tr:hover {
    background: #f8fafc;
  }

  .reference-badge {
    background: #f1f4f9;
    padding: 0.2rem 0.6rem;
    border-radius: 4px;
    font-size: 0.7rem;
    font-family: monospace;
    color: #334155;
  }

  /* Badge retard */
  .badge-retard {
    padding: 0.2rem 0.6rem;
    border-radius: 4px;
    font-size: 0.65rem;
    font-weight: 600;
    color: white;
  }
  .badge-retard.danger { background: ${COLORS.danger}; }
  .badge-retard.warning { background: ${COLORS.warning}; }

  /* Loading */
  .loading-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 60vh;
  }
  .loading-ring-wrapper {
    position: relative;
    width: 70px;
    height: 70px;
  }
  .loading-ring {
    position: absolute;
    width: 100%;
    height: 100%;
    border-radius: 50%;
    border: 3px solid #e9edf2;
    border-top-color: ${COLORS.primary};
    animation: spin 0.8s linear infinite;
  }
  .loading-ring-inner {
    position: absolute;
    top: 8px;
    left: 8px;
    right: 8px;
    bottom: 8px;
    border-radius: 50%;
    border: 2px solid #e9edf2;
    border-bottom-color: ${COLORS.secondary};
    animation: spin 0.6s linear infinite reverse;
  }
  @keyframes spin { to { transform: rotate(360deg); } }

  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(6px); }
    to { opacity: 1; transform: translateY(0); }
  }
  .fade-in {
    animation: fadeIn 0.3s ease-out;
  }

  /* Action bouton renouveler */
  .btn-renew {
    background: ${COLORS.primary}15;
    color: ${COLORS.primary};
    border: none;
    padding: 0.2rem 0.8rem;
    border-radius: 4px;
    font-size: 0.75rem;
    font-weight: 500;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    transition: all 0.2s;
  }
  .btn-renew:hover {
    background: ${COLORS.primary};
    color: white;
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(41,90,167,0.2);
  }

  @media (max-width: 768px) {
    .header-modern {
      flex-direction: column;
      align-items: stretch;
      gap: 0.8rem;
      padding: 1rem;
    }
    .header-left {
      flex-wrap: wrap;
    }
  }
`;

// ===== UTILITAIRES =====
const formatMontant = (montant) => {
  return new Intl.NumberFormat('fr-FR').format(montant) + ' FCFA';
};

const formatDateSimple = (dateString) => {
  return new Date(dateString).toLocaleDateString('fr-FR');
};

const formatDate = (dateString) => {
  const options = { year: 'numeric', month: 'long', day: 'numeric' };
  return new Date(dateString).toLocaleDateString('fr-FR', options);
};

export default function AbonnementsExpires() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [eglises, setEglises] = useState([]);
  const [selectedEglise, setSelectedEglise] = useState('all');
  const [expiredData, setExpiredData] = useState({
    abonnements: [],
    total: 0,
    montantTotal: 0
  });
  const [stats, setStats] = useState({
    totalEglises: 0,
    totalExpires: 0,
    montantTotal: 0,
    moyenneParEglise: 0
  });

  const fetchEglises = async () => {
    try {
      const userStr = localStorage.getItem('user');
      if (!userStr) return;
      const user = JSON.parse(userStr);
      const userId = user.id || user.id_utilisateur;
      const response = await fetch(`https://josephsbusiness.com/api/eglises.php?user_id=${userId}&type=siege&limit=1000`, {
        headers: { 'Accept': 'application/json' }
      });
      const result = await response.json();
      if (result.success) {
        setEglises(result.eglises || []);
        setStats(prev => ({ ...prev, totalEglises: result.eglises?.length || 0 }));
      }
    } catch (err) {
      console.error('Erreur chargement églises:', err);
    }
  };

  const fetchExpiredAbonnements = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const userStr = localStorage.getItem('user');
      if (!userStr) {
        navigate('/pages/signin2');
        return;
      }
      const user = JSON.parse(userStr);
      const userId = user.id || user.id_utilisateur;
      let url = `https://josephsbusiness.com/api/abonnements.php?user_id=${userId}&status=expire&limit=1000`;
      if (selectedEglise !== 'all') {
        url += `&eglise=${encodeURIComponent(selectedEglise)}`;
      }

      const response = await fetch(url, { headers: { 'Accept': 'application/json' } });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const result = await response.json();

      if (result.success) {
        const abonnements = result.abonnements || [];
        const montantTotal = abonnements.reduce((sum, abon) => sum + (parseFloat(abon.montant) || 0), 0);
        const eglisesUniques = [...new Set(abonnements.map(a => a.matri_eglise))];

        setExpiredData({ abonnements, total: abonnements.length, montantTotal });
        setStats({
          totalEglises: eglises.length,
          totalExpires: abonnements.length,
          montantTotal: montantTotal,
          moyenneParEglise: eglisesUniques.length > 0 ? (montantTotal / eglisesUniques.length) : 0
        });
      } else {
        setError(result.message || 'Erreur de chargement');
      }
    } catch (err) {
      console.error('Erreur:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedEglise, navigate, eglises.length]);

  useEffect(() => { fetchEglises(); }, []);
  useEffect(() => { fetchExpiredAbonnements(); }, [fetchExpiredAbonnements]);

  const getEgliseInfo = (matricule) => {
    if (!matricule) return { nom: 'Non spécifié', ville: '-', province: '-', tel: '-', email: '-' };
    const eglise = eglises.find(e => e.matricule === matricule);
    return eglise
      ? { nom: eglise.nom, ville: eglise.ville || '-', province: eglise.province || '-', tel: eglise.tel || '-', email: eglise.email || '-' }
      : { nom: matricule, ville: '-', province: '-', tel: '-', email: '-' };
  };

  const calculerJoursRetard = (dateFin) => {
    const aujourdhui = new Date();
    const fin = new Date(dateFin);
    const diffTime = Math.abs(aujourdhui - fin);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const exportToExcel = () => {
    const fileName = `abonnements_expires_${new Date().toISOString().split('T')[0]}.xlsx`;
    const exportData = expiredData.abonnements.map(abon => {
      const egliseInfo = getEgliseInfo(abon.matri_eglise);
      return {
        'Matricule abonnement': abon.matri_abon,
        'Église': egliseInfo.nom,
        'Ville': egliseInfo.ville,
        'Province': egliseInfo.province,
        'Téléphone': egliseInfo.tel,
        'Email': egliseInfo.email,
        'Date début': formatDateSimple(abon.date_deb),
        'Date fin': formatDateSimple(abon.date_fin),
        'Montant': abon.montant,
        'Jours de retard': calculerJoursRetard(abon.date_fin),
        'Date souscription': formatDateSimple(abon.date_at)
      };
    });

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(exportData);
    XLSX.utils.sheet_add_aoa(ws, [
      ['ABONNEMENTS EXPIRÉS'],
      [`Généré le ${new Date().toLocaleDateString('fr-FR')}`],
      [`Filtre: ${selectedEglise === 'all' ? 'Toutes les églises' : getEgliseInfo(selectedEglise).nom}`],
      [`Total: ${expiredData.total} abonnements expirés`],
      [`Montant total: ${expiredData.montantTotal} FCFA`],
      []
    ], { origin: 'A1' });
    XLSX.utils.book_append_sheet(wb, ws, "Abonnements expirés");
    XLSX.writeFile(wb, fileName);
  };

   if (loading) {
     return (
         <>
             <Header />
             <div className="main main-app p-3 p-lg-4 dashboard-styled">
                 <div className="loading-wrapper">
                     <div className="text-center">
                         {/* Logo ou icône animé */}
                         {/* <div className="loading-icon-wrapper mb-4">
                             <div className="loading-icon-pulse">
                                 <i className="ri-building-line" style={{ fontSize: '3.5rem', color: COLORS.primary }}></i>
                             </div>
                         </div> */}
 
                         {/* Anneau de chargement amélioré */}
                         <div className="loading-ring-wrapper mb-4">
                             <div className="loading-ring"></div>
                             <div className="loading-ring-inner"></div>
                             <div className="loading-ring-dot"></div>
                         </div>
 
                         {/* Titre avec effet de gradient */}
                         <h5 className="fw-bold loading-title" style={{ 
                             color: COLORS.primary,
                             background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.secondary})`,
                             WebkitBackgroundClip: 'text',
                             WebkitTextFillColor: 'transparent',
                             backgroundClip: 'text',
                             fontSize: '1.4rem'
                         }}>
                             Chargement en cours
                         </h5>
 
                         {/* Message avec animation de points */}
                         <p className="text-muted small loading-message">
                             Récupération des données
                             <span className="loading-dots">
                                 <span>.</span><span>.</span><span>.</span>
                             </span>
                         </p>
 
                         {/* Barre de progression stylisée */}
                         <div className="loading-progress-wrapper mt-4">
                             <div className="loading-progress-track">
                                 <div className="loading-progress-bar"></div>
                             </div>
                         </div>
                     </div>
                 </div>
             </div>
             <Footer />
 
             {/* Styles intégrés pour le loading */}
             <style>{`
                 .loading-wrapper {
                     display: flex;
                     justify-content: center;
                     align-items: center;
                     min-height: 60vh;
                 }
 
                 /* Icône animée */
                 .loading-icon-wrapper {
                     position: relative;
                     width: 80px;
                     height: 80px;
                     margin: 0 auto;
                 }
                 .loading-icon-pulse {
                     animation: iconPulse 1.8s ease-in-out infinite;
                 }
                 @keyframes iconPulse {
                     0%, 100% { transform: scale(1); opacity: 1; }
                     50% { transform: scale(1.1); opacity: 0.6; }
                 }
 
                 /* Anneaux de chargement */
                 .loading-ring-wrapper {
                     position: relative;
                     width: 70px;
                     height: 70px;
                     margin: 0 auto;
                 }
                 .loading-ring {
                     position: absolute;
                     width: 100%;
                     height: 100%;
                     border-radius: 50%;
                     border: 3px solid #e9edf2;
                     border-top-color: ${COLORS.primary};
                     animation: spin 1s cubic-bezier(0.6, 0.2, 0.4, 0.8) infinite;
                 }
                 .loading-ring-inner {
                     position: absolute;
                     top: 8px;
                     left: 8px;
                     right: 8px;
                     bottom: 8px;
                     border-radius: 50%;
                     border: 2px solid #e9edf2;
                     border-bottom-color: ${COLORS.secondary};
                     animation: spin 0.7s cubic-bezier(0.4, 0.0, 0.2, 1) infinite reverse;
                 }
                 .loading-ring-dot {
                     position: absolute;
                     top: 50%;
                     left: 50%;
                     transform: translate(-50%, -50%);
                     width: 12px;
                     height: 12px;
                     border-radius: 50%;
                     background: ${COLORS.primary};
                     box-shadow: 0 0 20px ${COLORS.primary}40;
                     animation: dotPulse 1.2s ease-in-out infinite;
                 }
                 @keyframes spin {
                     to { transform: rotate(360deg); }
                 }
                 @keyframes dotPulse {
                     0%, 100% { transform: translate(-50%, -50%) scale(0.8); opacity: 0.6; }
                     50% { transform: translate(-50%, -50%) scale(1.2); opacity: 1; }
                 }
 
                 /* Titre avec dégradé (déjà géré en inline) */
                 .loading-title {
                     margin-bottom: 0.25rem;
                 }
 
                 /* Message avec points animés */
                 .loading-message {
                     font-size: 0.9rem;
                     color: #94a3b8;
                 }
                 .loading-dots span {
                     animation: dots 1.4s infinite;
                     opacity: 0;
                 }
                 .loading-dots span:nth-child(1) { animation-delay: 0s; }
                 .loading-dots span:nth-child(2) { animation-delay: 0.2s; }
                 .loading-dots span:nth-child(3) { animation-delay: 0.4s; }
                 @keyframes dots {
                     0% { opacity: 0; }
                     50% { opacity: 1; }
                     100% { opacity: 0; }
                 }
 
                 /* Barre de progression */
                 .loading-progress-wrapper {
                     max-width: 250px;
                     margin: 0 auto;
                 }
                 .loading-progress-track {
                     width: 100%;
                     height: 4px;
                     background: #e9edf2;
                     border-radius: 4px;
                     overflow: hidden;
                 }
                 .loading-progress-bar {
                     width: 0%;
                     height: 100%;
                     background: linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary}, ${COLORS.primary});
                     background-size: 200% 100%;
                     border-radius: 4px;
                     animation: progress 2s ease-in-out infinite;
                 }
                 @keyframes progress {
                     0% { width: 0%; background-position: 200% 0; }
                     50% { width: 70%; background-position: 100% 0; }
                     100% { width: 100%; background-position: 0% 0; }
                 }
             `}</style>
         </>
     );
 }
  return (
    <>
      <style>{styles}</style>
      <Header />
      <div className="main main-app p-3 p-lg-4 dashboard-styled">
        <div className="container-fluid px-0">

          {/* ===== EN‑TÊTE MODERNE ===== */}
          <div className="header-modern mb-4 fade-in">
            <div className="header-left">
              <div className="header-icon-wrapper">
                <i className="ri-error-warning-line"></i>
              </div>
              <div>
                <h5 className="gradient-text">Abonnements expirés</h5>
                <div className="d-flex flex-wrap gap-2">
                  <span className="badge-modern">
                    <i className="ri-bar-chart-line"></i> {expiredData.total} expirés
                  </span>
                  <span className="badge-modern" style={{ background: `${COLORS.danger}15`, color: COLORS.danger }}>
                    <i className="ri-money-euro-circle-line"></i> {formatMontant(expiredData.montantTotal)}
                  </span>
                  <span className="badge-modern">
                    <i className="ri-calendar-line"></i> {new Date().toLocaleDateString('fr-FR')}
                  </span>
                </div>
              </div>
            </div>
            <div className="d-flex gap-2 flex-wrap">
              <Button size="sm" className="btn-modern-success" onClick={exportToExcel}>
                <i className="ri-file-excel-line"></i> Excel
              </Button>
              <Button size="sm" className="btn-modern-primary" onClick={() => navigate('/abonnements/liste')}>
                <i className="ri-arrow-left-line"></i> Tous les abonnements
              </Button>
            </div>
          </div>

          {/* ===== ERREUR ===== */}
          {error && (
            <Alert variant="danger" className="border-0 shadow-sm rounded-3 mb-3" style={{ borderRadius: '4px' }}>
              <i className="ri-error-warning-line me-2"></i>
              {error}
            </Alert>
          )}

          {/* ===== FILTRE ===== */}
          <Card className="filter-card mb-4 fade-in">
            <Card.Body className="p-3">
              <Row className="g-2 align-items-end">
                <Col md={8}>
                  <Form.Label className="fw-semibold text-white small" style={{ opacity: 0.8 }}>Filtrer par église</Form.Label>
                  <Form.Select
                    value={selectedEglise}
                    onChange={(e) => setSelectedEglise(e.target.value)}
                  >
                    <option value="all">Toutes les églises ({eglises.length})</option>
                    {eglises.map(eglise => (
                      <option key={eglise.matricule} value={eglise.matricule}>
                        {eglise.nom} {eglise.ville ? `- ${eglise.ville}` : ''}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
                <Col md={4}>
                  <Button size="sm" className=" w-100" style={{background:COLORS.secondary}} onClick={fetchExpiredAbonnements}>
                    <i className="ri-refresh-line me-1"></i> Actualiser
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* ===== KPI CARDS ===== */}
          <Row className="g-3 mb-4">
            <Col xs={6} md={3}>
              <div className="kpi-card kpi-primary">
                <div className="kpi-icon"><i className="ri-building-line"></i></div>
                <div className="kpi-content">
                  <span className="kpi-label">Églises</span>
                  <h3 className="kpi-value">{stats.totalEglises}</h3>
                </div>
              </div>
            </Col>
            <Col xs={6} md={3}>
              <div className="kpi-card kpi-danger">
                <div className="kpi-icon"><i className="ri-calendar-line"></i></div>
                <div className="kpi-content">
                  <span className="kpi-label">Expirés</span>
                  <h3 className="kpi-value">{expiredData.total}</h3>
                </div>
              </div>
            </Col>
            <Col xs={6} md={3}>
              <div className="kpi-card kpi-warning">
                <div className="kpi-icon"><i className="ri-money-euro-circle-line"></i></div>
                <div className="kpi-content">
                  <span className="kpi-label">Montant total</span>
                  <h3 className="kpi-value">{formatMontant(expiredData.montantTotal)}</h3>
                </div>
              </div>
            </Col>
            <Col xs={6} md={3}>
              <div className="kpi-card kpi-info">
                <div className="kpi-icon"><i className="ri-bar-chart-grouped-line"></i></div>
                <div className="kpi-content">
                  <span className="kpi-label">Moyenne/église</span>
                  <h3 className="kpi-value">{formatMontant(stats.moyenneParEglise)}</h3>
                </div>
              </div>
            </Col>
          </Row>

          {/* ===== TABLEAU ===== */}
          <Card className="table-card">
            <div className="table-header-secondary d-flex justify-content-between align-items-center px-3 py-2">
              <span className="fw-semibold"><i className="ri-list-check me-1"></i> Liste des abonnements expirés</span>
              <Badge bg="light" text="dark" className="rounded-pill px-3">{expiredData.total} abonnement{expiredData.total > 1 ? 's' : ''}</Badge>
            </div>
            <Card.Body className="p-0">
              <div className="table-responsive">
                <Table hover className="mb-0 align-middle table-modern">
                  <thead>
                    <tr>
                      <th className="px-2">N°</th>
                      <th className="px-2">Matricule</th>
                      <th className="px-2">Église</th>
                      <th className="px-2">Ville</th>
                      <th className="px-2">Début</th>
                      <th className="px-2">Fin</th>
                      <th className="px-2">Montant</th>
                      <th className="px-2">Retard</th>
                      <th className="px-2 text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {expiredData.abonnements.length > 0 ? (
                      expiredData.abonnements.map((abon, index) => {
                        const egliseInfo = getEgliseInfo(abon.matri_eglise);
                        const joursRetard = calculerJoursRetard(abon.date_fin);
                        const isCritique = joursRetard > 30;

                        return (
                          <tr key={abon.id_abon}>
                            <td className="px-2 text-muted">{index + 1}</td>
                            <td className="px-2"><span className="reference-badge">{abon.matri_abon}</span></td>
                            <td className="px-2">
                              <div className="fw-semibold">{egliseInfo.nom}</div>
                              {egliseInfo.tel !== '-' && (
                                <small className="text-muted"><i className="ri-phone-line me-1"></i>{egliseInfo.tel}</small>
                              )}
                            </td>
                            <td className="px-2">
                              <div>{egliseInfo.ville}</div>
                              <small className="text-muted">{egliseInfo.province}</small>
                            </td>
                            <td className="px-2">{formatDateSimple(abon.date_deb)}</td>
                            <td className="px-2">{formatDateSimple(abon.date_fin)}</td>
                            <td className="px-2 fw-bold" style={{ color: COLORS.primary }}>{formatMontant(abon.montant)}</td>
                            <td className="px-2">
                              <span className={`badge-retard ${isCritique ? 'danger' : 'warning'}`}>
                                {joursRetard} jour{joursRetard > 1 ? 's' : ''}
                              </span>
                            </td>
                            <td className="px-2 text-center">
                              <button
                                className="btn-renew"
                                onClick={() => navigate(`/abonnements/renouveler/${abon.id_abon}`, {
                                  state: { abonnement: abon }
                                })}
                                title="Renouveler"
                              >
                                <i className="ri-refresh-line"></i> Renouveler
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="9" className="text-center py-4">
                          <i className="ri-checkbox-circle-line" style={{ fontSize: '2.5rem', color: COLORS.success, display: 'block', marginBottom: '0.5rem' }}></i>
                          <h6 className="fw-bold mb-1">Aucun abonnement expiré</h6>
                          <p className="text-muted small">
                            {selectedEglise !== 'all'
                              ? "Cette église n'a pas d'abonnements expirés"
                              : "Tous les abonnements sont à jour"}
                          </p>
                          {selectedEglise !== 'all' && (
                            <Button size="sm" className="btn-modern-outline" onClick={() => setSelectedEglise('all')}>
                              Voir toutes les églises
                            </Button>
                          )}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>

          <Footer />
        </div>
      </div>
    </>
  );
}